import { HeaderCell } from '@/components/ats/Table/tableCells';
import { Table } from '@/components/ats';
import { ratingOptionsConfig } from '@/constants/scorecard-configs';
import { KText } from '@/ui-library/components';
import { Avatar, Box, Flex, Icon } from '@chakra-ui/react';
import { Calendar } from '@kula-ai/icons/outline/Calendar';
import { LuClipboardCheck } from 'react-icons/lu';
import { Eye } from '@kula-ai/icons/outline/Eye';
import dayjs from 'dayjs';
import { Hackerrank, Hackerearth, CriteriaCorp } from '@/assets/icons/ats';
import { useKFlags } from '@/hooks/use-k-flags';
import { SectionLabel } from '@kula-ai/components';

const iconItems: Record<string, any> = {
  review: Eye,
  interview: Calendar,
  assessment: LuClipboardCheck,
  hackerrank: Hackerrank,
  hackerearth_assessment: Hackerearth,
  criteriacorp_assessment: CriteriaCorp,
};

interface ScorecardPDFHistoryProps {
  scorecards: any[];
}

// Map record_type to icon kind
const getIconKind = (recordType: string) => {
  const kindMap: Record<string, string> = {
    AtsInterview: 'interview',
    AtsReview: 'review',
    AtsAssessment: 'assessment',
  };
  return kindMap[recordType] || 'review';
};

/**
 * PDF History table - simplified for print
 */
export const ScorecardPDFHistory = ({
  scorecards,
}: ScorecardPDFHistoryProps) => {
  const filteredScorecards = scorecards.filter(
    (scorecard) => scorecard.status === 'submitted'
  );

  const { feTransferCandidate } = useKFlags();

  const reviewerCell = {
    Header: () => <HeaderCell title="Interviewer/Reviewer" />,
    width: 200,
    accessor: 'reviewer',
    title: 'Interviewer/Reviewer',
    Cell: (cell: any) => {
      const name = cell?.value?.name ?? '';
      // display:table + fixed layout: ellipsis works in screen and Chromium PDF/print (flex + ellipsis often does not)
      return (
        <Box
          className="pdf-history-reviewer-cell"
          display="table"
          width="100%"
          maxW="250px"
          sx={{ tableLayout: 'fixed' }}
        >
          <Box as="span" display="table-row">
            <Box
              as="span"
              display="table-cell"
              w="32px"
              verticalAlign="middle"
              pr="8px"
            >
              <Avatar
                className="avatar-sm"
                src={cell?.value?.avatar}
                w="24px"
                h="24px"
                minW="24px"
                minH="24px"
                borderRadius="6px"
                name={name}
                overflow="hidden"
              />
            </Box>
            <Box
              as="span"
              display="table-cell"
              verticalAlign="middle"
              overflow="hidden"
              maxW={0}
              title={name}
            >
              <KText
                className="pdf-history-reviewer-name"
                size="sm-medium"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                display="block"
                width="100%"
              >
                {name}
              </KText>
            </Box>
          </Box>
        </Box>
      );
    },
  };

  const ratingCell = {
    Header: () => <HeaderCell title="Rating" />,
    width: 120,
    accessor: 'rating',
    title: 'Rating',
    Cell: (cell: any) => {
      const { value = '' } = cell;
      const config = ratingOptionsConfig.find(
        (option) => option.value === value
      );

      if (!value || !config) {
        return (
          <KText size="sm" color="secondary-500">
            -
          </KText>
        );
      }

      return (
        <Flex
          gap="4px"
          alignItems="center"
          borderRadius="4px"
          bg={config.bgColor}
          p="2px 6px"
          w="fit-content"
          whiteSpace="nowrap"
          flexShrink={0}
          className={value === 'strong_yes' ? 'checked_strong_yes' : ''}
        >
          <Icon
            as={config.icon}
            w="16px"
            h="16px"
            color={config.selectedColor}
            flexShrink={0}
          />
          <KText size="xs-medium" color={config.selectedColor} whiteSpace="nowrap">
            {config.label}
          </KText>
        </Flex>
      );
    },
  };

  const activityCell = {
    Header: () => <HeaderCell title="Activity" />,
    width: 160,
    accessor: 'record',
    title: 'Activity',
    Cell: (cell: any) => {
      const recordType = cell?.row?.original?.record_type;
      const iconKind = getIconKind(recordType);
      const activityName = cell?.value?.name || '-';

      // tableLayout.css uses display:flex on td — flex items default min-width:auto so chips
      // won't shrink; ellipsis never runs. Outer box: minW=0 + maxW caps width; inner chip:
      // max-content for short labels, maxWidth 100% when long so text can shrink + ellipsize.
      return (
        <Box w="100%" minW={0} maxW="180px" overflow="hidden">
          <Flex
            display="inline-flex"
            alignItems="center"
            gap="4px"
            border="1px solid"
            borderColor="border-color"
            borderRadius="4px"
            p="2px 6px"
            overflow="hidden"
            verticalAlign="middle"
            title={activityName}
            sx={{ width: 'max-content', maxWidth: '100%', minWidth: 0 }}
          >
            <Icon
              as={iconItems[iconKind] || Calendar}
              width="12px"
              height="12px"
              color="ats-primary"
              flexShrink={0}
            />
            <Box minW={0} flex="1 1 0" overflow="hidden">
              <KText
                className="pdf-history-activity-stage-name"
                size="xs"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                display="block"
                width="100%"
              >
                {activityName}
              </KText>
            </Box>
          </Flex>
        </Box>
      );
    },
  };

  const stageCell = {
    Header: () => <HeaderCell title="Stage" />,
    width: 130,
    accessor: 'stage',
    title: 'Stage',
    Cell: (cell: any) => {
      const stageName = cell?.value?.name || '-';
      return (
        <Box w="100%" minW={0} maxW="150px" overflow="hidden">
          <Box
            display="inline-block"
            border="1px solid"
            borderColor="border-color"
            borderRadius="4px"
            p="2px 6px"
            overflow="hidden"
            verticalAlign="middle"
            title={stageName}
            sx={{ width: 'max-content', maxWidth: '100%' }}
          >
            <KText
              className="pdf-history-activity-stage-name"
              size="xs"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              display="block"
              width="100%"
            >
              {stageName}
            </KText>
          </Box>
        </Box>
      );
    },
  };

  const submittedCell = {
    Header: () => <HeaderCell title="Submitted on" />,
    width: 140,
    accessor: feTransferCandidate ? 'submitted_at' : 'updated_at',
    title: 'Submitted on',
    Cell: (cell: any) => {
      const { row: { original } = { original: {} } } = cell ?? {};
      const submittedAt = dayjs(cell?.value);
      const createdAt = dayjs(original?.created_at);
      const dayDiff = submittedAt.diff(createdAt, 'day');

      if (!submittedAt.isValid() || !createdAt.isValid()) {
        return '';
      }

      return (
        <Flex alignItems="center" w="100%">
          <Flex direction="row" alignItems="center" w="fit-content">
            <KText fontSize="sm" w="fit-content" p="6px">
              {dayDiff === 0
                ? 'Within a day'
                : `After ${dayDiff} day${dayDiff !== 1 ? 's' : ''}`}
            </KText>
          </Flex>
        </Flex>
      );
    },
  };

  const tableConfig = [
    reviewerCell,
    ratingCell,
    activityCell,
    stageCell,
    submittedCell,
  ];

  if (!filteredScorecards?.length) {
    return null;
  }

  return (
    <Box className="pdf-history-table-outer" w="100%">
      <Flex flexDir="column" mb="0" className="history-section">
        <Box className="history-header">
          <SectionLabel showDivider size="lg" className="mb-4">
            History
          </SectionLabel>
        </Box>

        {/* Table — pdf-history-table: print/PDF border + ellipsis fixes in printStyles.css */}
        <Box className="pdf-history-table">
          <Table
            columns={tableConfig}
            data={filteredScorecards || []}
            tableId="pdf_history_scorecard"
            skipPagination={true}
            layoutStyles={{
              px: '0',
              borderTop: 'none',
              borderBottom: 'none',
              height: 'auto',
            }}
            layoutScrollContainerStyles={{ style: { overflow: 'visible' } }}
            tableContainerStyles={{
              className: 'pdf-history-table-root',
              overflow: 'visible',
              h: 'auto',
              minH: 0,
              maxH: 'none',
              flex: 'none',
            }}
            tableLayoutWrapperStyles={{
              overflow: 'visible',
              flex: 'none',
              minH: 0,
            }}
          />
        </Box>
      </Flex>
    </Box>
  );
};
